/**
 * The forge's name in the forge page header, renamed in place: the pencil
 * turns it into a text box; Enter or the check saves, Escape or the cross
 * cancels. A forge with no name yet (it predates names) shows its arche id.
 */
import { useState } from 'react';
import { Check, Loader2, Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/** Mirrors FORGE_NAME_MAX_CHARS (`forges.name` is VARCHAR(80)). */
export const FORGE_NAME_MAX = 80;

/** What to call a forge on screen. */
export function forgeDisplayName(forge: { name?: string | null; archeId: string }): string {
  return forge.name?.trim() || forge.archeId;
}

export interface ForgeNameEditorProps {
  forge: { name?: string | null; archeId: string };
  onRename: (name: string) => Promise<void>;
}

export function ForgeNameEditor({ forge, onRename }: ForgeNameEditorProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = forgeDisplayName(forge);

  const begin = () => {
    setDraft(forge.name ?? '');
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    const next = draft.trim();
    if (!next) {
      setError('Give the forge a name.');
      return;
    }
    if (next === forge.name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onRename(next);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The name could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex min-w-0 items-center gap-1">
        <h1 className="truncate text-sm font-semibold" title={forge.archeId}>
          {name}
        </h1>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={begin}
          aria-label="Rename forge"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 items-center gap-1">
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void save();
          if (e.key === 'Escape') setEditing(false);
        }}
        maxLength={FORGE_NAME_MAX}
        aria-label="Forge name"
        className="h-8 w-44 sm:w-64"
        autoFocus
        disabled={saving}
      />
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={() => void save()}
        disabled={saving}
        aria-label="Save name"
      >
        {saving ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Check className="h-3.5 w-3.5" />
        )}
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={() => setEditing(false)}
        disabled={saving}
        aria-label="Cancel rename"
      >
        <X className="h-3.5 w-3.5" />
      </Button>
      {error && (
        <span role="alert" className="text-xs text-destructive">
          {error}
        </span>
      )}
    </div>
  );
}
